const HANDOFF_STORAGE_PREFIX = 'gutdb:tcm-clinical-handoff:'
const ANALYSIS_CONTEXT_STORAGE_PREFIX = 'gutdb:tcm-clinical-context:'
const TCM_STORAGE_PREFIXES = [HANDOFF_STORAGE_PREFIX, ANALYSIS_CONTEXT_STORAGE_PREFIX]

export const TCM_CLINICAL_HANDOFF_QUERY = 'tcmClinicalContext'
export const TCM_CLINICAL_WORKFLOW_KEY = 'tcm.clinical_analysis.v1'
export const TCM_CLINICAL_HANDOFF_TTL_MS = 5 * 60 * 1000
export const TCM_CLINICAL_DRAFT_REQUEST = 'gutdb:tcm-clinical:draft-request'
export const TCM_CLINICAL_DRAFT_ACCEPTED = 'xbios:tcm-clinical:draft-accepted'
export const TCM_CLINICAL_DRAFT_ACK = 'xbios:tcm-clinical:draft-ack'
export const TCM_CLINICAL_DRAFT_VERSION = 2
export const TCM_CLINICAL_DRAFT_MAX_BYTES = 128 * 1024

const INPUT_KINDS = new Set([
  'clinicalData', 'dataDictionary', 'studyProtocol', 'researchNotes',
])
const STAGES = new Set([
  'data-preparation', 'descriptive-analysis', 'statistical-analysis-plan',
  'statistical-analysis', 'clinical-interpretation', 'clinical-report',
])

const text = (value, maxLength) => typeof value === 'string'
  ? value.trim().slice(0, maxLength)
  : ''

const scalarText = (value, maxLength) => (
  typeof value === 'string' || typeof value === 'number'
    ? String(value).trim().slice(0, maxLength)
    : ''
)

const requestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const productTemplate = () => ({
  key: 'tcm-clinical-analysis-product-2',
  name: '中医药临床数据分析',
  stages: [
    'data-preparation', 'descriptive-analysis', 'statistical-analysis-plan',
    'statistical-analysis', 'clinical-interpretation', 'clinical-report',
  ],
})

const currentIdentity = (identityStorage = window.localStorage) => {
  try {
    const user = JSON.parse(identityStorage?.getItem('user') || 'null')
    const identityId = scalarText(user?.identityId, 128)
    const identityUsername = text(user?.identityUsername, 255)
    return identityId && identityUsername ? { identityId, identityUsername } : null
  } catch {
    return null
  }
}

const expectedPersonalProjectRef = identity => `PERSONAL::${identity.identityUsername}`

const projectDirectoryName = value => {
  const normalized = text(value, 255)
  if (!normalized || normalized.includes('/') || normalized.includes('\\')
    || normalized === '..' || [...normalized].some(character => character.charCodeAt(0) < 32)) {
    return ''
  }
  return normalized
}

const relativeFilePath = value => {
  const normalized = text(value, 1000)
  if (!normalized || normalized.startsWith('/') || normalized.includes('\\')) return ''
  const parts = normalized.split('/')
  if (parts.some(part => !part || part === '.' || part === '..')) return ''
  return normalized
}

/** 一次性个性化交接在 sessionStorage 中的随机键。 */
export const tcmClinicalHandoffStorageKey = id =>
  `${HANDOFF_STORAGE_PREFIX}${text(id, 256)}`

/** 同一分析再次进入对话时复用的项目/运行上下文键。 */
export const tcmClinicalAnalysisContextStorageKey = analysisId =>
  `${ANALYSIS_CONTEXT_STORAGE_PREFIX}${text(analysisId, 64)}`

const normalizedInputs = inputs => (Array.isArray(inputs) ? inputs : [])
  .map(input => ({
    kind: text(input?.kind, 32),
    relativePath: relativeFilePath(input?.relativePath),
    ...(text(input?.sheetName, 255) ? { sheetName: text(input.sheetName, 255) } : {}),
  }))
  .filter(input => INPUT_KINDS.has(input.kind) && input.relativePath)

const normalizedArtifactRefs = refs => [...new Set((Array.isArray(refs) ? refs : [])
  .map(relativeFilePath)
  .filter(Boolean))].slice(0, 128)

const validInputCardinality = (rawInputs, inputs) => {
  if (!Array.isArray(rawInputs) || rawInputs.length < 1 || rawInputs.length > 128
    || inputs.length !== rawInputs.length) return false
  const counts = inputs.reduce((result, input) => ({
    ...result,
    [input.kind]: (result[input.kind] || 0) + 1,
  }), {})
  return (counts.clinicalData || 0) >= 1
    && (counts.dataDictionary || 0) <= 1
    && (counts.studyProtocol || 0) <= 1
    && (counts.researchNotes || 0) <= 1
}

const removeStorageKey = (storage, key) => {
  try {
    storage?.removeItem(key)
  } catch {
    // 受限浏览器环境中 sessionStorage 可能不可写，按无上下文处理。
  }
}

const handoffBelongsToAnalysisAndIdentity = (
  handoffId,
  analysisId,
  storage,
  identityStorage,
) => {
  const normalizedHandoffId = text(handoffId, 256)
  const normalizedAnalysisId = text(analysisId, 64)
  const identity = currentIdentity(identityStorage)
  if (!normalizedHandoffId || !normalizedAnalysisId || !identity) return false
  try {
    const value = JSON.parse(
      storage.getItem(tcmClinicalHandoffStorageKey(normalizedHandoffId)) || 'null',
    )
    return value?.requestId === normalizedHandoffId
      && value?.workflowKey === TCM_CLINICAL_WORKFLOW_KEY
      && text(value?.analysisId, 64) === normalizedAnalysisId
      && text(value?.identity?.identityId, 128) === identity.identityId
      && text(value?.identity?.identityUsername, 255) === identity.identityUsername
      && value?.project?.kind === 'PERSONAL'
      && text(value?.project?.projectRef, 255) === expectedPersonalProjectRef(identity)
  } catch {
    return false
  }
}

/**
 * 创建五分钟有效的一次性交接上下文。
 * PERSONAL 项目必须与当前登录身份一致，避免切换账号后复用旧项目。
 */
export function createTcmClinicalHandoff(
  input,
  storage = window.sessionStorage,
  identityStorage = window.localStorage,
) {
  const identity = currentIdentity(identityStorage)
  if (!identity) throw new Error('无法识别当前身份，请重新登录')

  const projectRef = text(input?.project?.projectRef, 255)
  const folderRef = projectDirectoryName(input?.project?.folderRef)
  const inputs = normalizedInputs(input?.inputs)
  if (projectRef !== expectedPersonalProjectRef(identity)) {
    throw new Error('当前项目与登录身份不匹配')
  }
  if (!folderRef || !validInputCardinality(input?.inputs, inputs)) {
    throw new Error('INCOMPLETE_TCM_CLINICAL_HANDOFF')
  }

  const id = requestId()
  const createdAt = Date.now()
  const analysisId = text(input?.analysisId, 64)
  const runId = text(input?.runId, 64)
  if (Boolean(analysisId) !== Boolean(runId)) {
    throw new Error('中医药临床分析编号和运行编号必须同时提供')
  }
  const currentStage = text(input?.currentStage, 64)
  const artifactRefs = normalizedArtifactRefs(input?.artifactRefs)
  storage.setItem(tcmClinicalHandoffStorageKey(id), JSON.stringify({
    requestId: id,
    workflowKey: TCM_CLINICAL_WORKFLOW_KEY,
    productTemplate: productTemplate(),
    identity,
    project: { kind: 'PERSONAL', projectRef, folderRef },
    inputs,
    analysisId,
    runId,
    ...(STAGES.has(currentStage) ? { currentStage } : {}),
    ...(artifactRefs.length ? { artifactRefs } : {}),
    createdAt,
    expiresAt: createdAt + TCM_CLINICAL_HANDOFF_TTL_MS,
  }))
  return id
}

/** 读取并校验一次性交接；过期、身份变化或分析不匹配时立即删除。 */
export function readTcmClinicalHandoff(
  id,
  storage = window.sessionStorage,
  identityStorage = window.localStorage,
) {
  const normalizedId = text(id, 256)
  if (!normalizedId) return null
  const storageKey = tcmClinicalHandoffStorageKey(normalizedId)
  const reject = () => {
    removeStorageKey(storage, storageKey)
    return null
  }

  try {
    const value = JSON.parse(storage.getItem(storageKey) || 'null')
    const identity = currentIdentity(identityStorage)
    const createdAt = value?.createdAt
    const expiresAt = value?.expiresAt
    const storedIdentityId = text(value?.identity?.identityId, 128)
    const storedIdentityUsername = text(value?.identity?.identityUsername, 255)
    const projectRef = text(value?.project?.projectRef, 255)
    const folderRef = projectDirectoryName(value?.project?.folderRef)
    const now = Date.now()

    if (!value || !identity
      || value.requestId !== normalizedId
      || value.workflowKey !== TCM_CLINICAL_WORKFLOW_KEY
      || value.project?.kind !== 'PERSONAL'
      || storedIdentityId !== identity.identityId
      || storedIdentityUsername !== identity.identityUsername
      || projectRef !== expectedPersonalProjectRef(identity)
      || !folderRef
      || !Number.isSafeInteger(createdAt)
      || !Number.isSafeInteger(expiresAt)
      || expiresAt !== createdAt + TCM_CLINICAL_HANDOFF_TTL_MS
      || createdAt > now
      || expiresAt <= now) return reject()

    const inputs = normalizedInputs(value.inputs)
    if (!validInputCardinality(value.inputs, inputs)) return reject()
    const analysisId = text(value.analysisId, 64)
    const runId = text(value.runId, 64)
    if (Boolean(analysisId) !== Boolean(runId)) return reject()
    const currentStage = text(value.currentStage, 64)
    if (currentStage && !STAGES.has(currentStage)) return reject()
    const artifactRefs = normalizedArtifactRefs(value.artifactRefs)
    if (Array.isArray(value.artifactRefs) && artifactRefs.length !== value.artifactRefs.length) return reject()
    return {
      requestId: normalizedId,
      workflowKey: TCM_CLINICAL_WORKFLOW_KEY,
      productTemplate: productTemplate(),
      project: { kind: 'PERSONAL', projectRef, folderRef },
      inputs,
      analysisId,
      runId,
      ...(currentStage ? { currentStage } : {}),
      ...(artifactRefs.length ? { artifactRefs } : {}),
    }
  } catch {
    return reject()
  }
}

/** xBIOS 完成 ACK 后消费一次性交接记录。 */
export function removeTcmClinicalHandoff(id, storage = window.sessionStorage) {
  const normalizedId = text(id, 256)
  if (normalizedId) removeStorageKey(storage, tcmClinicalHandoffStorageKey(normalizedId))
}

/** 保存分析级项目、运行和输入绑定，供运行页多次发起个性化调整。 */
export function saveTcmClinicalAnalysisContext(
  analysisId,
  input,
  storage = window.sessionStorage,
  identityStorage = window.localStorage,
) {
  const normalizedAnalysisId = text(analysisId, 64)
  if (!normalizedAnalysisId) throw new Error('缺少中医药临床分析编号')
  const identity = currentIdentity(identityStorage)
  if (!identity) throw new Error('无法识别当前身份，请重新登录')
  const projectRef = text(input?.project?.projectRef, 255)
  const folderRef = projectDirectoryName(input?.project?.folderRef)
  const normalized = normalizedInputs(input?.inputs)
  const runId = text(input?.runId, 64)
  if (projectRef !== expectedPersonalProjectRef(identity)
    || !folderRef || !runId || !validInputCardinality(input?.inputs, normalized)) {
    throw new Error('INCOMPLETE_TCM_CLINICAL_HANDOFF')
  }
  const contextKey = tcmClinicalAnalysisContextStorageKey(normalizedAnalysisId)
  const value = {
    workflowKey: TCM_CLINICAL_WORKFLOW_KEY,
    identity,
    // 个人项目引用可由当前登录身份唯一推导，长期分析上下文无需重复保存用户名。
    // 一次性 handoff 仍会在发送前通过 readTcmClinicalAnalysisContext 重建完整引用。
    project: { kind: 'PERSONAL', folderRef },
    inputs: normalized,
    analysisId: normalizedAnalysisId,
    runId,
    updatedAt: Date.now(),
  }
  try {
    // analysis 级绑定在当前登录会话内可复用；一次性 handoff 另存随机键并在 ACK 后消费。
    storage.setItem(contextKey, JSON.stringify(value))
  } catch {
    throw new Error('无法保存中医药临床分析上下文')
  }
  return normalizedAnalysisId
}

/** 按当前登录身份读取分析级绑定，并迁移/清理旧版存储格式。 */
export function readTcmClinicalAnalysisContext(
  analysisId,
  storage = window.sessionStorage,
  identityStorage = window.localStorage,
) {
  const normalizedAnalysisId = text(analysisId, 64)
  if (!normalizedAnalysisId) return null
  const contextKey = tcmClinicalAnalysisContextStorageKey(normalizedAnalysisId)
  let raw = ''
  try {
    raw = storage.getItem(contextKey) || ''
  } catch {
    return null
  }
  if (!raw) {
    removeStorageKey(storage, contextKey)
    return null
  }
  try {
    const value = JSON.parse(raw)
    const identity = currentIdentity(identityStorage)
    const storedProjectRef = text(value?.project?.projectRef, 255)
    const projectRef = identity ? expectedPersonalProjectRef(identity) : ''
    const folderRef = projectDirectoryName(value?.project?.folderRef)
    const normalized = normalizedInputs(value?.inputs)
    const runId = text(value?.runId, 64)
    if (!identity
      || value?.workflowKey !== TCM_CLINICAL_WORKFLOW_KEY
      || text(value?.analysisId, 64) !== normalizedAnalysisId
      || text(value?.identity?.identityId, 128) !== identity.identityId
      || text(value?.identity?.identityUsername, 255) !== identity.identityUsername
      || value?.project?.kind !== 'PERSONAL'
      || (storedProjectRef && storedProjectRef !== projectRef)
      || !folderRef || !runId
      || !validInputCardinality(value?.inputs, normalized)) {
      removeStorageKey(storage, contextKey)
      return null
    }
    // 兼容并就地清理旧版包含 projectRef 的分析上下文。
    if (storedProjectRef) {
      try {
        storage.setItem(contextKey, JSON.stringify({
          ...value,
          project: { kind: 'PERSONAL', folderRef },
        }))
      } catch {
        // 清理失败不影响本次读取；身份与项目引用已经完成校验。
      }
    }
    return {
      workflowKey: TCM_CLINICAL_WORKFLOW_KEY,
      project: { kind: 'PERSONAL', projectRef, folderRef },
      inputs: normalized,
      analysisId: normalizedAnalysisId,
      runId,
    }
  } catch {
    // 兼容旧版 analysisId -> handoffId 引用，并原地迁移为可复用绑定。
    const legacyId = text(raw, 256)
    const context = readTcmClinicalHandoff(legacyId, storage, identityStorage)
    if (!context || context.analysisId !== normalizedAnalysisId) {
      removeStorageKey(storage, contextKey)
      return null
    }
    saveTcmClinicalAnalysisContext(
      normalizedAnalysisId, context, storage, identityStorage,
    )
    removeTcmClinicalHandoff(legacyId, storage)
    return readTcmClinicalAnalysisContext(
      normalizedAnalysisId, storage, identityStorage,
    )
  }
}

/** 删除分析级绑定及其可能引用的旧版一次性交接。 */
export function removeTcmClinicalAnalysisContext(
  analysisId,
  storage = window.sessionStorage,
  identityStorage = window.localStorage,
) {
  const normalizedAnalysisId = text(analysisId, 64)
  if (!normalizedAnalysisId) return
  const contextKey = tcmClinicalAnalysisContextStorageKey(normalizedAnalysisId)
  let legacyId = ''
  try {
    const raw = storage.getItem(contextKey) || ''
    try {
      JSON.parse(raw)
    } catch {
      legacyId = text(raw, 256)
    }
  } catch {
    // 即使无法读取，也继续删除 analysisId 对应的键。
  }
  removeStorageKey(storage, contextKey)
  if (handoffBelongsToAnalysisAndIdentity(
    legacyId,
    normalizedAnalysisId,
    storage,
    identityStorage,
  )) removeTcmClinicalHandoff(legacyId, storage)
}

/** 退出登录或切换身份时清除全部产品二短期上下文。 */
export function clearTcmClinicalSessionStorage(storage = window.sessionStorage) {
  try {
    const keys = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith('gutdb:tcm-microbiota-handoff:') || TCM_STORAGE_PREFIXES.some(prefix => key?.startsWith(prefix))) keys.push(key)
    }
    keys.forEach(key => removeStorageKey(storage, key))
  } catch {
    // 清理失败不应阻断退出登录或身份切换。
  }
}

const serializedByteLength = value => {
  const serialized = JSON.stringify(value)
  if (window.TextEncoder) return new window.TextEncoder().encode(serialized).byteLength
  return new window.Blob([serialized]).size
}

/** 将已校验的分析上下文转换为 xBIOS 草稿协议；始终禁止自动发送。 */
export function createTcmClinicalDraftRequest(context, transferRequestId = requestId()) {
  const inputs = normalizedInputs(context?.inputs)
  const request = {
    type: TCM_CLINICAL_DRAFT_REQUEST,
    version: TCM_CLINICAL_DRAFT_VERSION,
    requestId: text(transferRequestId, 128),
    autoSubmit: false,
    payload: {
      workflowKey: TCM_CLINICAL_WORKFLOW_KEY,
      projectRef: text(context?.project?.projectRef, 255),
      folderRef: projectDirectoryName(context?.project?.folderRef),
      inputs,
      ...(text(context?.analysisId, 64) ? { analysisId: text(context.analysisId, 64) } : {}),
      ...(text(context?.runId, 64) ? { runId: text(context.runId, 64) } : {}),
      ...(STAGES.has(text(context?.currentStage, 64))
        ? { currentStage: text(context.currentStage, 64) } : {}),
      ...(normalizedArtifactRefs(context?.artifactRefs).length
        ? { artifactRefs: normalizedArtifactRefs(context.artifactRefs) } : {}),
    },
  }
  if (!request.requestId || !request.payload.projectRef || !request.payload.folderRef
    || Boolean(request.payload.analysisId) !== Boolean(request.payload.runId)
    || !validInputCardinality(context?.inputs, inputs)) {
    throw new Error('中医药临床对话上下文不完整')
  }
  if (serializedByteLength(request) > TCM_CLINICAL_DRAFT_MAX_BYTES) {
    throw new Error('中医药临床对话上下文超过 128 KiB')
  }
  return request
}

/**
 * 管理 GutDB 与 iframe 中 xBIOS 的 postMessage 双阶段握手。
 * 先等待 accepted，再等待草稿真正写入目标会话；超时或协议错误会返回稳定错误码。
 */
export function createTcmClinicalDraftHandoffBridge({
  readContext = readTcmClinicalHandoff,
  removeContext = removeTcmClinicalHandoff,
  createRequest = createTcmClinicalDraftRequest,
  getTargetWindow,
  getTargetOrigin,
  onTimeout,
  storage = window.sessionStorage,
  identityStorage = window.localStorage,
  ackTimeout = 10000,
  setTimer = window.setTimeout,
  clearTimer = window.clearTimeout,
}) {
  let ready = false
  let handoffRequestId = ''
  let pending = null
  let transferMessage = null
  let timer = null

  const clearPending = () => {
    if (timer !== null) clearTimer(timer)
    timer = null
    pending = null
  }

  const failure = (errorCode, errorMessage) => ({
    sent: false, errorCode, errorMessage,
  })

  return {
    setReady(value) {
      ready = Boolean(value)
    },
    activate(requestIdValue) {
      const id = text(requestIdValue, 256)
      const context = readContext(id, storage, identityStorage)
      if (!context) return false
      clearPending()
      transferMessage = null
      handoffRequestId = id
      return true
    },
    send() {
      if (pending) return failure('HANDOFF_IN_PROGRESS', '正在等待 xBIOS 确认，请稍候')
      const context = readContext(
        handoffRequestId, storage, identityStorage,
      )
      if (!context) return failure(
        'HANDOFF_CONTEXT_EXPIRED', '上下文已过期或身份已变化，请重新选择',
      )
      if (!ready) return failure('IFRAME_NOT_READY', 'xBIOS 尚未就绪，请稍后重试')
      const targetWindow = getTargetWindow?.()
      const targetOrigin = getTargetOrigin?.()
      if (!targetWindow || !targetOrigin) return failure(
        'IFRAME_NOT_READY', '无法确认 xBIOS 同源窗口，请稍后重试',
      )
      let message
      try {
        // 超时重放相同请求，子页面可返回缓存 ACK 或继续未完成的交接。
        message = transferMessage || createRequest(context)
        transferMessage = message
      } catch (cause) {
        return failure('HANDOFF_INVALID', cause?.message || '对话上下文不合法')
      }
      pending = {
        requestId: message.requestId,
        targetWindow,
        targetOrigin,
      }
      targetWindow.postMessage(message, targetOrigin)
      timer = setTimer(() => {
        const timedOutRequestId = pending?.requestId
        clearPending()
        if (timedOutRequestId) onTimeout?.(timedOutRequestId)
      }, ackTimeout)
      return { sent: true, requestId: message.requestId }
    },
    acknowledge(event) {
      const message = event?.data
      if (!pending
        || event.source !== pending.targetWindow
        || event.origin !== pending.targetOrigin
        || message?.type !== TCM_CLINICAL_DRAFT_ACK
        || message?.version !== TCM_CLINICAL_DRAFT_VERSION
        || message?.requestId !== pending.requestId
        || typeof message?.ok !== 'boolean') return null
      const transferRequestId = pending.requestId
      clearPending()
      if (message.ok === true) {
        const consumedHandoffId = handoffRequestId
        // ACK 只消费这次传输请求；analysis 级项目/运行绑定仍可供再次进入复用。
        removeContext(handoffRequestId, storage)
        handoffRequestId = ''
        return { ok: true, requestId: transferRequestId, handoffRequestId: consumedHandoffId }
      }
      return {
        ok: false,
        requestId: transferRequestId,
        errorCode: text(message.errorCode, 100) || 'XBIO_HANDOFF_REJECTED',
        errorMessage: text(message.errorMessage, 500) || 'xBIOS 未接受对话上下文',
      }
    },
    accept(event) {
      const message = event?.data
      if (!pending
        || event.source !== pending.targetWindow
        || event.origin !== pending.targetOrigin
        || message?.type !== TCM_CLINICAL_DRAFT_ACCEPTED
        || message?.version !== TCM_CLINICAL_DRAFT_VERSION
        || message?.requestId !== pending.requestId
        || message?.ok !== true) return null
      return {
        requestId: pending.requestId,
        projectDirectory: text(message.projectDirectory, 2048),
        sessionId: text(message.sessionId, 256),
      }
    },
    cancel({ remove = false } = {}) {
      clearPending()
      if (remove && handoffRequestId) {
        removeContext(handoffRequestId, storage)
      }
      handoffRequestId = ''
      transferMessage = null
    },
    pendingRequestId() {
      return pending?.requestId || ''
    },
  }
}
